import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Build,
  ArrowForward,
  Warning,
  CheckCircle,
  Category
} from '@mui/icons-material';
import MaintenanceList from './MaintenanceList';
import MaintenanceForm from './MaintenanceForm';
import { useNavigate } from 'react-router-dom';

const MaintenancePage = () => {
  const [open, setOpen] = useState(false);
  const [editMaintenance, setEditMaintenance] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  const handleAdd = () => {
    setEditMaintenance(null);
    setIsEditing(true);
    setOpen(true);
  };

  const handleEdit = (maintenance) => {
    setEditMaintenance(maintenance);
    setIsEditing(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMaintenance(null);
    setIsEditing(false);
  };

  const handleFormClose = (shouldRefetch) => {
    handleClose();
    if (shouldRefetch) {
      // Trigger a refetch of the data instead of full page reload
      // The parent component should handle the refetch
    }
  };

  return (
    <Box sx={{ 
      p: 4, 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', 
      minHeight: '100vh' 
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: '#f59e0b',
              width: 56,
              height: 56,
              mr: 2,
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)'
            }}
          >
            <Build sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5
              }}
            >
              Maintenance Records
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              Track vehicle maintenance by category for better reporting and scheduling
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Action Cards */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(245, 158, 11, 0.2)',
              }
            }} onClick={handleAdd}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: '#f59e0b',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)'
                  }}
                >
                  <AddIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Add Maintenance Record
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Log a new maintenance or service record with category tracking
                </Typography>
                <Chip
                  label="Quick Add"
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(99, 102, 241, 0.2)',
              }
            }} onClick={() => navigate('/fleet/vehicles')}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: '#6366f1',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)'
                  }}
                >
                  <ArrowForward sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Vehicle Fleet
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  View vehicle maintenance history and category-based reports
                </Typography>
                <Chip
                  label="View All"
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.2)',
              }
            }} onClick={() => navigate('/fleet/inspections')}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: '#10b981',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                  }}
                >
                  <Category sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Category Reports
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Track maintenance by category (oil changes, tire rotations, etc.)
                </Typography>
                <Chip
                  label="Track"
                  color="success"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Maintenance List */}
      <Card sx={{
        borderRadius: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            Maintenance History by Category
          </Typography>
          <MaintenanceList onEdit={handleEdit} />
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editMaintenance ? (isEditing ? 'Edit Maintenance' : 'View Maintenance') : 'Add Maintenance'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <MaintenanceForm 
            recordId={editMaintenance?.id}
            onClose={handleFormClose}
            isEditing={isEditing}
            onToggleEdit={() => setIsEditing(!isEditing)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MaintenancePage; 